// @/components/auth/AuthRouter.jsx
// Auth guard — checks auth state, detects tokens, handles redirects

import React, { useState, useEffect } from "react";
import { navigate } from "gatsby";
import { authService } from '@/services/auth';
import TokenHandler from "./TokenHandler";
import { isPageReload, clearRedirectLoopCounter, trackRedirect } from '@/utils/session-utils';
import { Button, ButtonGroup } from "@/components/reusable/button";

const AuthRouter = ({ children, requireAuth = false }) => {
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authCheckComplete, setAuthCheckComplete] = useState(false);
  const [hasToken, setHasToken] = useState(false);
  const [isLogoutPage, setIsLogoutPage] = useState(false);
  const [isKeycloakLogoutRedirect, setIsKeycloakLogoutRedirect] = useState(false);
  const [isReload, setIsReload] = useState(false);
  const [clientReady, setClientReady] = useState(false);

  // SSR-safe: check client-side values after mount
  useEffect(() => {
    const logoutPage = window.location.pathname === '/logout' || window.location.pathname === '/logout/';
    setIsLogoutPage(logoutPage);
    setIsReload(isPageReload());

    if (logoutPage) {
      const params = new URLSearchParams(window.location.search);
      if (params.has('code') || params.has('session_state') || params.has('iss') || params.has('error')) {
        sessionStorage.setItem('controlledLogout', 'true');
        sessionStorage.setItem('sessionTimeout', 'true');
        sessionStorage.setItem('onLogoutPage', 'true');
        sessionStorage.setItem('skipAuthProcessing', 'true');
        setIsKeycloakLogoutRedirect(true);
      }
    }
    setClientReady(true);
  }, []);

  // Check for token in URL
  useEffect(() => {
    if (!clientReady) return;
    if (isLogoutPage || isKeycloakLogoutRedirect) {
      setLoading(false);
      setAuthCheckComplete(true);
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token') || params.get('invitation') || params.get('invitationToken');
    if (urlToken) setHasToken(true);
    setLoading(false);
  }, [clientReady, isLogoutPage, isKeycloakLogoutRedirect]);

  // Check authentication status
  useEffect(() => {
    if (!clientReady || isLogoutPage || isKeycloakLogoutRedirect) return;

    if (hasToken || !requireAuth) {
      setAuthCheckComplete(true);
      setLoading(false);
      return;
    }

    const checkAuth = async () => {
      try {
        if (isReload) clearRedirectLoopCounter();
        const authenticated = await authService.initialize();
        setIsAuthenticated(authenticated);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.error('Auth check error:', error);
        setIsAuthenticated(false);
      } finally {
        setAuthCheckComplete(true);
        setLoading(false);
      }
    };
    checkAuth();
  }, [clientReady, hasToken, isReload, isLogoutPage, isKeycloakLogoutRedirect, requireAuth]);

  // Never redirect from logout page
  if (isLogoutPage || isKeycloakLogoutRedirect) return children;

  // Handle unauthenticated state on protected routes
  if (requireAuth && authCheckComplete && !isAuthenticated) {
    const inLogoutState =
      sessionStorage.getItem('controlledLogout') === 'true' ||
      sessionStorage.getItem('sessionTimeout') === 'true' ||
      sessionStorage.getItem('onLogoutPage') === 'true';

    if (inLogoutState) return children;

    const redirectCount = trackRedirect();
    if (redirectCount >= 3) {
      return (
        <div className="py-12 mx-auto max-w-3xl text-center">
          <div className="space-y-4">
            <h1 className="text-rose-500">Authentication Error</h1>
            <h4 className="font-medium">We detected a redirection loop in the authentication process.</h4>
          </div>
          <ButtonGroup className="justify-center! gap-5! mt-12!">
            <Button variant="outline" onClick={() => { clearRedirectLoopCounter(); window.location.href = '/logout/'; }}>
              Go to Logout
            </Button>
            <Button onClick={() => { clearRedirectLoopCounter(); window.location.href = '/'; }}>
              Try Again
            </Button>
          </ButtonGroup>
        </div>
      );
    }

    navigate("/", { replace: true });
    return null;
  }

  if (loading) return children;
  if (hasToken) return <TokenHandler />;
  return children;
};

export default AuthRouter;
